import React, { useState, useEffect } from 'react'
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalCloseButton,
    ModalBody,
    ModalFooter,
    Button,
    FormControl,
    FormLabel,
    Input,
    FormErrorMessage,
    Stack,
    Checkbox,
    Box,
    useToast,
    Image,
    Alert,
    AlertIcon
} from '@chakra-ui/react'
import RevolutCheckout from "@revolut/checkout";
import AsyncSelect from 'react-select/async';


const RegisterModal = ({ isOpen, onClose, mode = 'owner', invitedCompanyData = {}, token }) => {
    const toast = useToast()
    const [errors, setErrors] = useState({})
    const [formData, setFormData] = useState({
        name: '',
        surname: '',
        bookingEmail: '',
        bookingPhone: '',
        password: '',
        confirmPassword: '',
        companyName: '',
        registrationNumber: '',
        tradingName: '',
        accountsEmail: '',
        paymentDetails: '',
        revolutCustomerId: '',
        acceptTC: false,
        logo: null,
        logoPreview: null,
        selectedCompany: null,
    })
    const [showCardInput, setShowCardInput] = useState(false);
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

    useEffect(() => {
        if (mode === 'invite' && invitedCompanyData) {
            setFormData(prev => ({
                ...prev,
                companyName: invitedCompanyData.companyName || '',
                registrationNumber: invitedCompanyData.registrationNumber || '',
                tradingName: invitedCompanyData.tradingName || '',
                accountsEmail: invitedCompanyData.accountsEmail || '',
                paymentDetails: invitedCompanyData.paymentDetails || '',
                bookingEmail: invitedCompanyData.invitedEmail || '',
            }))
        }
    }, [mode, invitedCompanyData])

    const handleChange = (e) => {
        const { id, value } = e.target
        setFormData(prev => ({ ...prev, [id]: value }))

        setErrors(prevErrors => {
            const updatedErrors = { ...prevErrors }

            switch (id) {
                case 'name':
                    if (value.trim().length >= 3) delete updatedErrors.name
                    break
                case 'surname':
                    if (value.trim().length >= 3) delete updatedErrors.surname
                    break
                case 'bookingEmail':
                    if (emailRegex.test(value)) delete updatedErrors.bookingEmail
                    break
                case 'bookingPhone':
                    if (value.length > 0 && value.length <= 15) delete updatedErrors.bookingPhone
                    break
                case 'password':
                    if (value.length >= 6) delete updatedErrors.password
                    if (formData.confirmPassword === value) delete updatedErrors.confirmPassword
                    break
                case 'confirmPassword':
                    if (value === formData.password) delete updatedErrors.confirmPassword
                    break
                case 'accountsEmail':
                    if (!value || emailRegex.test(value)) delete updatedErrors.accountsEmail;
                    else updatedErrors.accountsEmail = true;
                    break
                default:
                    if (value.trim()) delete updatedErrors[id]
                    break
            }

            return updatedErrors
        })
    }

    const handleAddCard = async () => {

        const { name, surname, bookingEmail, bookingPhone } = formData;
        const newErrors = {};

        if (!name || name.trim().length < 3) newErrors.name = 'Name must be at least 3 characters';
        if (!surname || surname.trim().length < 3) newErrors.surname = 'Surname must be at least 3 characters';
        if (!bookingEmail || !emailRegex.test(bookingEmail)) newErrors.bookingEmail = 'Valid email is required';
        if (!bookingPhone || bookingPhone.length > 15) newErrors.bookingPhone = 'Valid phone number is required';

        if (Object.keys(newErrors).length > 0) {
            setErrors(prev => ({ ...prev, ...newErrors }));

            const messages = Object.values(newErrors).join('\n');

            toast({
                title: 'Please fix the following before adding card:',
                description: (
                    <Box as="ul" pl={4}>
                        {Object.values(newErrors).map((msg, idx) => (
                            <li key={idx}>{msg}</li>
                        ))}
                    </Box>
                ),
                status: 'error',
                variant: 'left-accent',
                duration: 5000,
                isClosable: true,
                position: 'bottom-right',
                containerStyle: {
                    maxWidth: '360px',
                    whiteSpace: 'normal'
                }
            });

            return;
        }

        try {
            const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/revolut/create-customer`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name: `${formData.name} ${formData.surname}`, email: formData.bookingEmail, phone:formData.bookingPhone })
            });

            const data = await res.json();
            if (!res.ok || !data.token) {
                setErrors(prev => ({
                    ...prev,
                    paymentDetails: data.error ? data.error?.message : data.message
                }));
                return;
            }

            setShowCardInput(true);
            setTimeout(async () => {
                const { createCardField } = await RevolutCheckout(data.token);
                const cardField = createCardField({
                    target: document.getElementById("card-field"),
                    onSuccess() {
                        toast({
                            title: 'Card added successfully',
                            status: 'success',
                            variant: 'left-accent',
                            duration: 3000,
                            isClosable: true,
                            position: 'bottom-right'
                        });
                        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/revolut/get-payment-id?orderId=${data.orderId}&customerId=${data.customerId}`, {
                            method: 'GET'
                        })
                            .then(res => res.json())
                            .then(cardData => {
                                if (cardData.paymentCardId) {
                                    setFormData(prev => ({
                                        ...prev,
                                        paymentDetails: cardData.paymentCardId,
                                        revolutCustomerId: cardData.revolutCustomerId
                                    }));
                                    setErrors(prevErrors => {
                                        const updatedErrors = { ...prevErrors };
                                        delete updatedErrors.paymentDetails;
                                        return updatedErrors;
                                    });
                                } else {
                                    toast({
                                        title: 'Error retrieving payment info',
                                        status: 'error',
                                        variant: 'left-accent',
                                        duration: 3000,
                                        position: 'bottom-right'
                                    });
                                }
                            });
                    },
                    onError(error) {
                        console.log(error.toString())
                        toast({
                            title: 'Card error',
                            description: error.toString(),
                            status: 'error',
                            variant: 'left-accent',
                            duration: 3000,
                            position: 'bottom-right'
                        });
                    }
                });

                document.getElementById("button-submit").onclick = () => {

                    cardField.submit({
                        email: formData.bookingEmail,
                        name: `${formData.name} ${formData.surname}`,
                        savePaymentMethodFor: 'merchant'
                    });
                };
            }, 100);

        } catch (err) {
            setErrors(prev => ({
                ...prev,
                paymentDetails: err.message || 'Could not start payment setup'
            }));
        }
    };

    const handleSubmit = async () => {
        const {
            name,
            surname,
            bookingEmail,
            bookingPhone,
            password,
            confirmPassword,
            companyName,
            registrationNumber,
            accountsEmail,
            paymentDetails,
        } = formData
        const newErrors = {}

        if (!name || name.trim().length < 3) newErrors.name = true
        if (!surname || surname.trim().length < 3) newErrors.surname = true
        if (!bookingEmail || !emailRegex.test(bookingEmail)) newErrors.bookingEmail = true
        if (!bookingPhone || bookingPhone.length > 15) newErrors.bookingPhone = true
        if (!password || password.length < 6) newErrors.password = true
        if (!confirmPassword || confirmPassword !== password) newErrors.confirmPassword = true


        if (mode === 'owner') {
            if (!formData.acceptTC) newErrors.acceptTC = true;
            if (!companyName) newErrors.companyName = true
            if (!registrationNumber) newErrors.registrationNumber = true
            // if (!accountsEmail || !emailRegex.test(accountsEmail)) newErrors.accountsEmail = true
            if (!paymentDetails) newErrors.paymentDetails = true
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors)
            toast({
                title: 'Please fix the highlighted fields.',
                status: 'error',
                variant: 'left-accent',
                duration: 4000,
                isClosable: true,
                position: 'bottom-right'
            })
            return
        }
        const formPayload = new FormData();
        Object.keys(formData).forEach((key) => {
            if (key === 'selectedCompany') return;
            if (formData[key] !== undefined && formData[key] !== null) {
                formPayload.append(key, formData[key]);
            }
        });
        if (formData.selectedCompany) {
            formPayload.append('selectedCompany', JSON.stringify(formData.selectedCompany));
        }


        formPayload.append('mode', mode);
        if (token) formPayload.append('token', token);
        try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/register`, {
                method: 'POST',
                body: formPayload
            })

            const data = await response.json()
            if (!response.ok) {
                throw new Error(data.message || 'Registration failed');
            }

            localStorage.setItem('authToken', data.authToken);
            localStorage.setItem('authUser', JSON.stringify(data.user));

            toast({
                title: 'Registration successful.',
                description: 'Your account was created.',
                status: 'success',
                variant: 'left-accent',
                duration: 4000,
                isClosable: true,
                position: 'bottom-right'
            })

            onClose()
            if (window.location.search) {
                const cleanUrl = window.location.origin + window.location.pathname;
                window.history.replaceState({}, document.title, cleanUrl);
            }
            window.location.reload();
        } catch (err) {
            toast({
                title: 'Registration failed.',
                description: err.message,
                position: 'bottom-right',
                status: 'error',
                variant: 'left-accent',
                duration: 4000,
                isClosable: true
            })
        }
    }
    function debounce(fn, delay) {
        let timeoutId;
        return function(...args) {
            clearTimeout(timeoutId);
            return new Promise((resolve) => {
                timeoutId = setTimeout(() => resolve(fn(...args)), delay);
            });
        };
    }

    const fetchCompanySuggestions = debounce(async (inputValue) => {
        if (!inputValue) return [];

        try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/companies?input=${inputValue}`);
            const data = await response.json();

            return (
                data?.suggestions?.map((item) => ({
                    label: `${item.companyName} (${item.registrationNumber})`,
                    value: item.registrationNumber,
                    data: item
                })) || []
            );
        } catch (error) {
            console.error('Company fetch error:', error.message);
            return [];
        }
    }, 700);

    return (
        <Modal isOpen={isOpen} onClose={onClose} isCentered size="xl">
            <ModalOverlay />
            <ModalContent p={6} bg="white" containerProps={{ zIndex: '99997' }}>
                <ModalHeader textAlign="center" fontWeight="bold">Register Your Account</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <Stack spacing={4}>
                        <FormControl id="name" isInvalid={errors.name} isRequired>
                            <FormLabel>Name</FormLabel>
                            <Input onChange={handleChange} />
                            {errors.name && <FormErrorMessage>Minimum 3 characters</FormErrorMessage>}
                        </FormControl>

                        <FormControl id="surname" isInvalid={errors.surname} isRequired>
                            <FormLabel>Surname</FormLabel>
                            <Input onChange={handleChange} />
                            {errors.surname && <FormErrorMessage>Minimum 3 characters</FormErrorMessage>}
                        </FormControl>

                        <FormControl id="bookingEmail" isInvalid={errors.bookingEmail} isRequired>
                            <FormLabel>Email</FormLabel>
                            <Input
                                type="email"
                                onChange={handleChange}
                                value={formData.bookingEmail}
                                isReadOnly={mode === "invite"}
                                bg={mode === "invite" ? "gray.100" : undefined}
                                color={mode === "invite" ? "gray.600" : undefined}
                                borderColor={mode === "invite" ? "gray.300" : undefined}
                            />
                            {errors.bookingEmail && <FormErrorMessage>Invalid email address</FormErrorMessage>}
                        </FormControl>

                        <FormControl id="bookingPhone" isInvalid={errors.bookingPhone} isRequired>
                            <FormLabel>Contact Number</FormLabel>
                            <Input onChange={handleChange} />
                            {errors.bookingPhone && <FormErrorMessage>Max 15 characters</FormErrorMessage>}
                        </FormControl>

                        <FormControl id="password" isInvalid={errors.password} isRequired>
                            <FormLabel>Password</FormLabel>
                            <Input type="password" onChange={handleChange} />
                            {errors.password && <FormErrorMessage>Minimum 6 characters</FormErrorMessage>}
                        </FormControl>

                        <FormControl id="confirmPassword" isInvalid={errors.confirmPassword} isRequired>
                            <FormLabel>Confirm Password</FormLabel>
                            <Input type="password" onChange={handleChange} />
                            {errors.confirmPassword && <FormErrorMessage>Passwords do not match</FormErrorMessage>}
                        </FormControl>

                        {mode === 'owner' && (
                            <>

                                <FormControl id="companySearch" isRequired>
                                    <FormLabel>Your Company details – type name or number</FormLabel>
                                    <AsyncSelect
                                        cacheOptions
                                        defaultOptions
                                        loadOptions={fetchCompanySuggestions}
                                        onChange={(selectedOption) => {
                                            if (selectedOption && selectedOption.data) {
                                                const { companyName, registrationNumber } = selectedOption.data;
                                                setFormData(prev => ({
                                                    ...prev,
                                                    companyName,
                                                    registrationNumber,
                                                    selectedCompany: selectedOption.data
                                                }));
                                            }
                                        }}
                                        placeholder="Type company name or number"
                                    />
                                </FormControl>


                                <FormControl id="companyName" isInvalid={errors.companyName} isRequired>
                                    <FormLabel>Company Name</FormLabel>
                                    <Input
                                        value={formData.companyName}
                                        onChange={handleChange}
                                        isReadOnly
                                        bg="gray.100"
                                        color="gray.600"
                                        borderColor="gray.300"
                                    />
                                    {errors.companyName && <FormErrorMessage>Required</FormErrorMessage>}
                                </FormControl>

                                <FormControl id="registrationNumber" isInvalid={errors.registrationNumber} isRequired>
                                    <FormLabel>Company Registration Number</FormLabel>
                                    <Input
                                        value={formData.registrationNumber}
                                        onChange={handleChange}
                                        isReadOnly
                                        bg="gray.100"
                                        color="gray.600"
                                        borderColor="gray.300"
                                    />
                                    {errors.registrationNumber && <FormErrorMessage>Required</FormErrorMessage>}
                                </FormControl>

                                <FormControl id="tradingName">
                                    <FormLabel>Trading Name (optional)</FormLabel>
                                    <Input onChange={handleChange} />
                                </FormControl>

                                <FormControl id="logo">
                                    <FormLabel>Company Logo</FormLabel>
                                    {formData.logoPreview && (
                                        <Image
                                            src={formData.logoPreview}
                                            alt="Logo Preview"
                                            mt={3}
                                            boxSize="100px"
                                            objectFit="contain"
                                            border="1px solid #ccc"
                                            borderRadius="md"
                                        />
                                    )}
                                    <Input
                                        type="file"
                                        accept="image/*"
                                        onChange={(e) => {
                                            const file = e.target.files[0];
                                            if (file) {
                                                setFormData((prev) => ({
                                                    ...prev,
                                                    logo: file,
                                                    logoPreview: URL.createObjectURL(file),
                                                }));
                                            }
                                        }}
                                        height="40px"
                                        padding="4px 8px"
                                        lineHeight="1.5"
                                        borderRadius="md"
                                        border="1px solid #ccc"
                                    />
                                </FormControl>

                                <FormControl id="accountsEmail" isInvalid={errors.accountsEmail} >
                                    <FormLabel>Accounts Email (where the payment checks will be sent)</FormLabel>
                                    <Input type="email" onChange={handleChange} />
                                    {errors.accountsEmail && <FormErrorMessage>Invalid email</FormErrorMessage>}
                                </FormControl>

                                <FormControl id="paymentDetails" isInvalid={errors.paymentDetails} isRequired>
                                    <FormLabel>Payment Details</FormLabel>
                                    <Button variant="solid-green-btn" fontWeight={600} width="auto" onClick={handleAddCard}>Add Payment Details</Button>
                                    {formData.paymentDetails && !errors.paymentDetails && (
                                        <Box mt={3}>
                                            <Alert status="success" borderRadius="md" variant='left-accent'>
                                                <AlertIcon />
                                                Card successfully saved.
                                            </Alert>
                                        </Box>
                                    )}
                                    {errors.paymentDetails && (
                                        <FormErrorMessage>{errors.paymentDetails}</FormErrorMessage>
                                    )}
                                </FormControl>
                                { showCardInput && (
                                    <>
                                        <div id="card-field"></div>
                                        <Button id="button-submit" variant="solid-green-btn" fontWeight={600} type="button">Submit</Button>
                                    </>
                                )}
                                <FormControl isRequired isInvalid={errors.acceptTC}>
                                    <Checkbox
                                        isChecked={formData.acceptTC}
                                        onChange={(e) => setFormData(prev => ({ ...prev, acceptTC: e.target.checked }))}
                                        colorScheme="green"
                                    >
                                        <Box fontSize="14px">
                                            I accept{' '}
                                            <a
                                                href="https://www.spacephoto.co.uk/terms-and-conditions/"
                                                target="_blank"
                                                rel="noreferrer"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                terms and conditions
                                            </a>{' '}
                                            *
                                        </Box>
                                    </Checkbox>
                                    {errors.acceptTC && <FormErrorMessage>You must accept the terms</FormErrorMessage>}
                                </FormControl>
                            </>
                        )}
                    </Stack>

                </ModalBody>
                <ModalFooter justifyContent="center">
                    <Button variant="solid-green-btn" fontWeight={600} onClick={handleSubmit}>
                        Register
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    )
}

export default RegisterModal
